"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.SpectatorCallFrame = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("react");
const ui_react_1 = require("@streem/ui-react");
const sdk_core_1 = require("@streem/sdk-core");
const tool_bar_1 = require("../tool_bar");
const __1 = require("../../..");
const room_session_1 = require("../../../context/room_session");
const use_guide_ui_1 = require("../../../hooks/use_guide_ui");
const use_bookmark_to_select_1 = require("../../../hooks/use_bookmark_to_select");
const use_camera_subject_1 = require("../../../hooks/use_camera_subject");
const successful_streemshot_1 = require("../successful_streemshot");
const video_frame_1 = require("../video_frame/video_frame");
const operators_1 = require("rxjs/operators");
const animations_1 = require("../../../animations");
const check_client_support_1 = require("./two_way_video/check_client_support");
const use_in_call_shortcuts_1 = require("../../../hooks/use_in_call_shortcuts");
const in_call_details_content_tray_1 = require("./in_call_details_content_tray");
const recording_indicator_1 = require("../video_frame/recording/recording_indicator");
function SpectatorCallFrame() {
    const ANIMATION_DELAY = 200;
    const ANIMATION_DURATION = 500;
    const preventAnimations = (0, react_1.useRef)(true);
    const [animating, setAnimating] = (0, react_1.useState)(false);
    const [faceToWorld, setFaceToWorld] = (0, react_1.useState)(false);
    const theme = (0, ui_react_1.useTheme)();
    const roomSession = (0, room_session_1.useRoomSession)();
    const [trayOpen, setTrayOpen] = (0, react_1.useState)(true);
    const { guideUI, setGuideUI } = (0, use_guide_ui_1.useGuideUI)();
    const [bookmarkToSelect] = (0, use_bookmark_to_select_1.useBookmarkToSelect)();
    const [videoControllers] = (0, __1.useObservable)(roomSession.media.videoControllers);
    const hasWorld = (0, use_camera_subject_1.hasWorldSubject)(roomSession.media.videoControllers);
    const fullScreen = hasWorld;
    const [showArrow, setShowArrow] = (0, react_1.useState)(false);
    const [flipArrow, setFlipArrow] = (0, react_1.useState)(false);
    const [remoteUser] = (0, __1.useObservable)(roomSession.user.remoteUser);
    const [localVideoEnabled] = (0, __1.useVideoEnabled)(roomSession.media.videoControllers.pipe((0, operators_1.scan)((acc, controllers) => {
        return controllers.find(controller => controller.ownerIsLocalUser) || acc;
    }, undefined)));
    const cameraSubjects = (0, use_camera_subject_1.useCameraSubject)(roomSession.media.videoControllers);
    const [remoteVideoEnabled] = (0, __1.useVideoEnabled)(roomSession.media.videoControllers.pipe((0, operators_1.scan)((acc, controllers) => {
        return controllers.find(controller => !controller.ownerIsLocalUser) || acc;
    }, undefined)));
    const [remoteIsARCapable] = (0, __1.useObservable)(roomSession.ar.remoteParticipantIsARCapable);
    const remoteShadowRef = (0, react_1.useRef)(null);
    const remoteRef = (0, react_1.useRef)(null);
    const localShadowRef = (0, react_1.useRef)(null);
    const localRef = (0, react_1.useRef)(null);
    (0, use_in_call_shortcuts_1.useInCallShortcuts)();
    (0, react_1.useEffect)(() => {
        if (bookmarkToSelect) {
            setTrayOpen(true);
        }
    }, [bookmarkToSelect]);
    const videoFrames = (0, react_1.useMemo)(() => {
        if (!videoControllers || !videoControllers.length) {
            return undefined;
        }
        return videoControllers
            .sort(controller => {
            return controller.ownerIsLocalUser ? 1 : -1;
        })
            .map(controller => {
            if (fullScreen) {
                setShowArrow(false);
            }
            const localPipView = faceToWorld && controller.ownerIsLocalUser;
            const remoteFullscreenView = faceToWorld && !controller.ownerIsLocalUser;
            const styles = {};
            /*
            To persist component styles after animations end,
            we update their style objects to match animation end keyframe styles
        */
            if (localPipView) {
                styles.width = '267px';
                styles.height = '150px';
                styles.bottom = '20px';
                styles.right = '20px';
                styles.position = 'absolute';
                styles.backgroundColor = theme.colors.grey90;
                // fade pip in/out on video feed toggle
                styles.transition = `opacity ${ANIMATION_DURATION}ms linear`;
                styles.opacity = localVideoEnabled ? '1' : '0';
                styles.zIndex = 3;
            }
            else if (remoteFullscreenView) {
                styles.position = 'absolute';
                styles.top = '0px';
                styles.left = '0px';
                styles.height = 'var(--auto-grid-height, 100%)';
                styles.width = '100%';
                styles.backgroundColor = theme.colors.black;
                styles.transition = `border-radius 200ms ease-in`;
                styles.borderRadius = '0px';
            }
            // Style correctly for 1WV view mode
            if (videoControllers.length === 1) {
                styles.backgroundColor = theme.colors.black;
            }
            // Don't allow drawing on your own camera, unless in onsite session
            if (!(0, sdk_core_1.isOnSiteRoomSession)(roomSession) && controller.ownerIsLocalUser) {
                styles.pointerEvents = 'none';
            }
            return ((0, jsx_runtime_1.jsx)(video_frame_1.VideoFrame, { ref: controller.ownerIsLocalUser ? localRef : remoteRef, videoController: controller, showArrow: showArrow, flipArrow: flipArrow, animationOptions: {
                    animating: controller.ownerIsLocalUser ? false : animating,
                    duration: ANIMATION_DURATION,
                    delay: ANIMATION_DELAY,
                }, cardOptions: {
                    constrainContainerToCanvas: !controller.ownerIsLocalUser && faceToWorld,
                    styles: styles,
                    localPipView,
                } }, controller.id));
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        videoControllers,
        fullScreen,
        showArrow,
        localRef,
        remoteRef,
        faceToWorld,
        localVideoEnabled,
        ANIMATION_DURATION,
        animating,
        trayOpen,
    ]);
    (0, react_1.useLayoutEffect)(() => {
        // This animation should only run when switching from FACE_TO_FACE to FACE_TO_WORLD or vice versa.  Skip running when the
        //      call initially starts.  When the calls starts, this component renders initially with an empty cameraSubjects array
        //      and then renders again with it populated.  Wait until cameraSubjects is populated before unsetting preventAnimations.
        if (preventAnimations.current && cameraSubjects.length > 1) {
            preventAnimations.current = false;
            return;
        }
        if (cameraSubjects.length > 1 &&
            remoteRef &&
            localRef &&
            remoteRef.current &&
            localRef.current &&
            localShadowRef &&
            localShadowRef.current &&
            remoteShadowRef &&
            remoteShadowRef.current &&
            !preventAnimations.current) {
            // transition from FACE_TO_FACE to FACE_TO_WORLD
            if (fullScreen) {
                setAnimating(true);
                const animationOptions = {
                    easing: 'ease-out',
                    duration: ANIMATION_DURATION,
                    delay: ANIMATION_DELAY,
                };
                const growRemoteElementToFillParentContainerKeyFrames = (0, animations_1.growElementToFillParentContainer)(remoteRef.current, remoteRef.current.offsetParent);
                const shrinkLocalElementToPipLocation = (0, animations_1.shrinkElementToBottomRightCornerOfParentContainer)(localRef.current, localRef.current.offsetParent, { right: 20, bottom: 20, width: 267, height: 150 });
                let canvasWrapperElement = null;
                // Do not animate remote canvas during tranistion if video feed is disabled
                if (remoteRef.current.querySelector('[data-queryselector="canvas-wrapper"]') &&
                    remoteVideoEnabled) {
                    canvasWrapperElement = remoteRef.current.querySelector('[data-queryselector=canvas-wrapper]');
                }
                if (canvasWrapperElement) {
                    canvasWrapperElement.animate(animations_1.flipAndBlurKeyframes, animationOptions);
                }
                remoteRef.current.animate(growRemoteElementToFillParentContainerKeyFrames, animationOptions);
                const localAnimation = localRef.current.animate(shrinkLocalElementToPipLocation, animationOptions);
                localAnimation.onfinish = () => {
                    setFaceToWorld(true);
                    setAnimating(false);
                };
                // transition from FACE_TO_WORLD to FACE_TO_FACE
            }
            else if (!fullScreen) {
                setAnimating(true);
                const animationOptions = {
                    easing: 'ease-out',
                    duration: ANIMATION_DURATION,
                    delay: ANIMATION_DELAY,
                };
                const moveRemoteElementToCoverShadowRemoteElement = (0, animations_1.moveElementToCoverTargetElement)(remoteRef.current, remoteShadowRef.current, remoteShadowRef.current.offsetParent);
                const moveLocalElementToCoverShadowLocalElement = (0, animations_1.moveElementToCoverTargetElement)(localRef.current, localShadowRef.current, localShadowRef.current.offsetParent);
                let canvasWrapperElement = null;
                if (remoteRef.current.querySelector('[data-queryselector=canvas-wrapper]') &&
                    remoteVideoEnabled) {
                    canvasWrapperElement = remoteRef.current.querySelector('[data-querySelector=canvas-wrapper]');
                }
                if (canvasWrapperElement) {
                    canvasWrapperElement.animate(animations_1.flipAndBlurKeyframes, animationOptions);
                }
                remoteRef.current.animate(moveRemoteElementToCoverShadowRemoteElement, animationOptions);
                // Reveal the pro's PiP video (if video OFF) before growing/moving back to grid position
                localRef.current.style.opacity = '1';
                const animation = localRef.current.animate(moveLocalElementToCoverShadowLocalElement, animationOptions);
                animation.onfinish = () => {
                    if (localRef && localRef.current) {
                        localRef.current.style.opacity = '';
                    }
                    setFaceToWorld(false);
                    setAnimating(false);
                };
            }
        }
        // eslint-disable-next-line react-hooks/rules-of-hooks,react-hooks/exhaustive-deps
    }, [hasWorld, cameraSubjects.length]);
    return ((0, jsx_runtime_1.jsx)(ui_react_1.TrayLayout, { isOpen: trayOpen, onToggleClick: () => {
            if (trayOpen) {
                document.activeElement.blur();
            }
            setTrayOpen(!trayOpen);
        }, toggleText: 'Details', stageContent: (0, jsx_runtime_1.jsxs)(Container, { children: [(0, jsx_runtime_1.jsx)(check_client_support_1.CheckClientSupport, {}), (0, jsx_runtime_1.jsx)(successful_streemshot_1.SuccessfulStreemshot, {}), (0, jsx_runtime_1.jsx)(recording_indicator_1.RecordingIndicator, { trayOpen: trayOpen }), (0, jsx_runtime_1.jsx)(ui_react_1.AutoGrid, Object.assign({ aspectRatio: 16 / 9, minimumGap: 10, options: {
                        fillScreenWhenOneChild: (videoControllers === null || videoControllers === void 0 ? void 0 : videoControllers.length) === 1,
                        setSmallGap: setFlipArrow,
                    } }, { children: videoFrames === null || videoFrames === void 0 ? void 0 : videoFrames.map(videoFrame => {
                        if (!videoFrame) {
                            return null;
                        }
                        const isLocalUser = videoFrame === null || videoFrame === void 0 ? void 0 : videoFrame.props.videoController.ownerIsLocalUser;
                        return ((0, jsx_runtime_1.jsx)(ShadowDiv, Object.assign({ ref: isLocalUser ? localShadowRef : remoteShadowRef }, { children: videoFrame }), videoFrame.props.videoController.id));
                    }) }))] }), toolbarCentralContent: (0, jsx_runtime_1.jsx)(tool_bar_1.CenterToolBar, { isTrayOpen: trayOpen, setShowArrow: setShowArrow, remoteUserName: remoteUser === null || remoteUser === void 0 ? void 0 : remoteUser.name }), toolbarRightContent: (0, jsx_runtime_1.jsx)(tool_bar_1.RightToolBar, { isTrayOpen: trayOpen, handleGuideToggle: () => setGuideUI(!guideUI), showGuideUI: guideUI, remoteIsARCapable: remoteIsARCapable }), trayContent: (0, jsx_runtime_1.jsx)(in_call_details_content_tray_1.InCallDetailsContentTray, { isTrayOpen: trayOpen }) }));
}
exports.SpectatorCallFrame = SpectatorCallFrame;
const Container = ui_react_1.styled.div({
    height: '100%',
    width: '100%',
    backgroundColor: 'black',
    display: 'flex',
    flexDirection: 'column',
});
const ShadowDiv = ui_react_1.styled.div `
    height: var(--item-height, 100%);
    width: var(--item-width, 100%);
`;
//# sourceMappingURL=spectator_call_frame.js.map